<template>
  <div class="w-full">
    <template v-for="item in links" :key="item">
      <NavDivider v-if="(item as Divider).rel === 'divider'" />
      <NavGroupTitle
        v-else-if="(item as GroupTitle).rel === 'groupTitle' && !collapsed"
        :title="(item as GroupTitle).title"
      />
      <NavAccordion
        v-else-if="(item as GroupTitle).rel === 'group' && !collapsed && !!item.items?.length"
        :icon="item.icon"
        :label="(item as GroupTitle).label"
        :items="item.items"
        :collapsed="collapsed"
        @menu:click="$emit('menu:click')"
      />
      <div v-else>
        <NavItem
          :to="(item as Navigatable).to"
          :count="(item as Navigatable).count || 0"
          :data-testid="(item as Navigatable).label.toLowerCase() + '-sidenav'"
          @click="$emit('menu:click')"
        >
          <component :is="(item as Navigatable).icon" class="h-5 w-5" />
          <span v-if="!collapsed">{{ (item as Navigatable).label }}</span>
        </NavItem>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import * as Icons from '@pulse/components/icons';

import { useSeasonalNav } from '~/composables/useSeasonalIcons';
import { CLAIM_AUTOMATION_TAB_PATHS } from '~/components/admin/automation/claim-automation-tabs';
import { customizationPages, FEATURE_FLAGS, FilterType } from '@constants';
import { storeToRefs } from 'pinia';
import { map, tap } from 'rxjs';

import { useMerchantStore } from '~/store/merchant';
import { useUserStore } from '~/store/user';
import { Capabilities } from '~/support/services/merchant.rest.service';

defineEmits(['menu:click']);

const user = useUserStore();
const store = useMerchantStore();
const { getFlag } = useLaunchDarkly();
// Reseller-owned brands can be configured to hide OP's finance surfaces (ENG-5850).
const { showFinance, showAccountDashboard, showChildAccountDashboard } = useFinanceVisibility();
// A reseller sees an Account Admin area for the Child Accounts beneath it (ENG-5850).
const { isParent, parentAccountId } = useAccountHierarchy();
// OP staff reach Account Admin via the Stores hand-off (`?accountId=`); feed it to the access check.
const navRoute = useRoute();
const canAccessAccountAdmin = useAccountAdminAccess(() => accountAdminTargetFromQuery(navRoute.query));
// Either end of a hierarchy has a child-level dashboard; a standalone account has neither.
const isInHierarchy = computed(() => isParent.value || !!parentAccountId.value);
const { currentStore, allStores } = storeToRefs(store);
const { isGlobalSupport } = storeToRefs(user);
const { claims: claimsSvc } = useGateway();
const filters = useFilters(FilterType.claim);

const claimsTodoCount = ref(0);
const warrantiesCount = ref(0);

// Fetch claims count when store changes
watch(
  [currentStore, () => store.multipleStoresSelected],
  () => {
    if ((currentStore.value?.id || store.multipleStoresSelected) && can(can.claim.read)) {
      claimsSvc.claims.counts
        .forStores({
          filters: JSON.stringify(
            filters.getQuery([
              {
                field: 'claimCategory',
                logicalOperator: 'AND',
                operator: 'not',
                type: 'string',
                value: 'WARRANTY',
              },
            ]),
          ),
        })
        .pipe(
          map(response => response.data?.new || 0),
          tap(count => {
            claimsTodoCount.value = count;
          }),
        )
        .subscribe({
          error: error => {
            console.error('Failed to fetch claims count:', error);
            claimsTodoCount.value = 0;
          },
        });
    } else {
      claimsTodoCount.value = 0;
    }
  },
  { immediate: true },
);

// Fetch warranties count when store changes
watch(
  [currentStore, () => store.multipleStoresSelected],
  () => {
    if ((currentStore.value?.id || store.multipleStoresSelected) && can(can.claim.read)) {
      claimsSvc.claims.warranties.counts
        .forStores(store.storeIdsArray, {
          filters: JSON.stringify([
            {
              field: 'claimCategory',
              logicalOperator: 'AND',
              operator: 'equals',
              type: 'string',
              value: 'WARRANTY',
            },
          ]),
        })
        .pipe(
          map((response: any) => response.data?.new || 0),
          tap(count => {
            warrantiesCount.value = count;
          }),
        )
        .subscribe({
          error: error => {
            console.error('Failed to fetch warranties count:', error);
            warrantiesCount.value = 0;
          },
        });
    } else {
      warrantiesCount.value = 0;
    }
  },
  { immediate: true },
);

type Navigatable = {
  icon: unknown;
  label: string;
  to: string;
  count?: number;
  activePaths?: string[];
};

type Divider = {
  rel: 'divider';
};

type GroupTitle = {
  rel: 'groupTitle';
  title: string;
};

type NavItem = Navigatable | Divider | GroupTitle | undefined;

const capabilities = computed<Capabilities>(() => {
  const allFalse = new Proxy(
    {},
    {
      get: () => false,
    },
  );

  if (!currentStore) {
    return allFalse;
  }

  if (!currentStore.value) {
    return allFalse;
  }

  return currentStore.value.capabilities;
});

const baseLinks = computed<NavItem[]>(
  () =>
    [
      can(can.analytics.read) && {
        items: [
          {
            icon: Icons.LineDashboard,
            label: 'Dashboard',
            to: '/dashboard',
          },
          {
            icon: Icons.LineCameraPlus,
            label: 'Gallery',
            to: '/gallery',
          },
          getFlag(FEATURE_FLAGS.ANALYTICS_API_V1, false) && {
            icon: Icons.ApiLogo,
            label: 'Analytics API',
            to: '/analytics/api',
          },
          // The two reseller-hierarchy dashboards (ENG-5850 Surface B). Two nav items rather than one
          // page with tabs: each carries its own visibility toggle and embeds a different signed
          // dashboard. The parent rollup only means anything to a Parent Account.
          isParent.value &&
            hasAccountEntitlements([can.analytics.read]) &&
            getFlag(FEATURE_FLAGS.ACCOUNT_PARENTS_ADMIN, false) &&
            showAccountDashboard.value && {
              icon: Icons.LineChart,
              label: 'Account Dashboard',
              to: '/analytics/account-dashboard',
            },
          isInHierarchy.value &&
            (!isParent.value || hasAccountEntitlements([can.analytics.read])) &&
            getFlag(FEATURE_FLAGS.ACCOUNT_PARENTS_ADMIN, false) &&
            showChildAccountDashboard.value && {
              icon: Icons.LineChart,
              label: 'Child Account Dashboard',
              to: '/analytics/child-account-dashboard',
            },
        ].filter(Boolean) as NavItem[],
        label: 'Analytics',
        rel: 'group',
      },
      can(can.claim.read) && {
        count: claimsTodoCount.value,
        icon: Icons.LineTruck,
        label: 'Shipping Claims',
        to: '/claims',
      },
      ((atLeastOneWarrantyStore.value && store.multipleStoresSelected) ||
        (!store.multipleStoresSelected && capabilities.value?.warranties)) &&
        getFlag(FEATURE_FLAGS.WARRANTIES, false) && {
          count: warrantiesCount.value,
          icon: Icons.LineShieldCheck,
          label: 'Warranty Claims',
          to: '/warranties',
        },
      can(can.order.read) && {
        icon: Icons.QueueList,
        label: 'Orders',
        to: '/orders',
      },
      !store.multipleStoresSelected &&
        can(can.product.read) &&
        getFlag(FEATURE_FLAGS.PRODUCTS, false) && {
          icon: Icons.LineShoppingBag,
          label: 'Products',
          to: '/products',
        },
      can(can.customers.read) &&
        getFlag(FEATURE_FLAGS.CUSTOMER_INFO_PAGES, false) && {
          icon: Icons.LineUsersAlt,
          label: 'Customers',
          to: '/customers',
        },
      !store.multipleStoresSelected &&
        capabilities.value?.returnsIndex &&
        can(can.returns.read) && {
          icon: Icons.LineBox,
          label: 'Returns',
          to: '/returns',
        },
      !store.multipleStoresSelected &&
        can(can.finance.read) &&
        showFinance.value && {
          icon: Icons.LineComparison,
          items: [
            can(can.payout.read) && {
              icon: Icons.LineMoneyWithdraw,
              label: 'Payouts',
              to: `/${currentStore?.value?.slug}/finance/payouts`,
            },
            can(can.invoice.read) && {
              icon: Icons.LineUsdCircle,
              label: 'Payments',
              to: `/${currentStore?.value?.slug}/finance/payments`,
            },
            can(can.financeAdmin.read) && {
              icon: Icons.LineFileLandscape,
              label: 'Admin',
              to: `/${currentStore?.value?.slug}/finance/admin`,
            },
          ].filter(Boolean) as NavItem[],
          label: 'Finance',
          rel: 'group',
        },
      can(can.reviews.read) && {
        icon: Icons.LineStar,
        label: 'Reviews',
        to: '/reviews',
      },
      !store.multipleStoresSelected &&
        capabilities.value?.customizations &&
        can(can.customizations.read) && {
          icon: Icons.LineComparison,
          items: [
            ...(capabilities.value?.customClaims
              ? [
                  {
                    icon: Icons[customizationPages.claims.icon.camelCase],
                    label: customizationPages.claims.label,
                    to: `/${currentStore?.value?.slug}/customizations/${customizationPages.claims.path}`,
                  },
                ]
              : []),
            ...(capabilities.value?.customEmails && getFlag(FEATURE_FLAGS.EMAILS, false)
              ? [
                  {
                    icon: Icons[customizationPages.emails.icon.camelCase],
                    label: customizationPages.emails.label,
                    to: `/${currentStore?.value?.slug}/customizations/${customizationPages.emails.path}`,
                  },
                ]
              : []),
            ...(capabilities.value?.customWidgets
              ? [
                  {
                    icon: Icons[customizationPages.widgets.icon.camelCase],
                    label: customizationPages.widgets.label,
                    to: `/${currentStore?.value?.slug}/customizations/${customizationPages.widgets.path}`,
                  },
                ]
              : []),
            ...(capabilities.value?.customInfoModals && getFlag(FEATURE_FLAGS.INFO_MODALS, false)
              ? [
                  {
                    icon: Icons[customizationPages.infoModals.icon.camelCase],
                    label: customizationPages.infoModals.label,
                    to: `/${currentStore?.value?.slug}/customizations/${customizationPages.infoModals.path}`,
                  },
                ]
              : []),
          ].filter(Boolean) as NavItem[],
          label: 'Customizations',
          rel: 'group',
        },
      !store.multipleStoresSelected &&
        !isGlobalSupport.value && {
          rel: 'divider',
        },
      can(can.storeCredit.read) &&
        storeCreditEnabled.value && {
          icon: Icons.LineTransaction,
          label: 'Store Credit',
          to: '/store-credit',
        },
      storeCreditEnabled.value && {
        rel: 'divider',
      },
      currentStore?.value &&
        !store.multipleStoresSelected &&
        can(can.teamMember.read) && {
          icon: Icons.LineUsersAlt,
          label: 'Team Members',
          to: `/${currentStore?.value?.slug}/team-members`,
        },
      currentStore?.value &&
        !store.multipleStoresSelected &&
        can(can.integrations.read) && {
          icon: Icons.ApiLogo,
          label: 'Integrations',
          to: '/integrations/active',
        },
      currentStore?.value &&
        !store.multipleStoresSelected &&
        can(can.marketplace.read) &&
        getFlag(FEATURE_FLAGS.MARKETPLACE, false) && {
          icon: Icons.LineShoppingBag,
          label: 'App Marketplace',
          to: `/${currentStore?.value?.slug}/marketplace`,
        },
      currentStore?.value &&
        !store.multipleStoresSelected &&
        can(can.settingsGeneral.read) && {
          icon: Icons.LineGlobe,
          label: 'Settings',
          to: `/${currentStore?.value?.slug}/settings`,
        },
      currentStore?.value &&
        !store.multipleStoresSelected &&
        capabilities.value?.splitTest &&
        can(can.abTests.read) && {
          icon: Icons.ArrowsRightLeft,
          label: 'A/B Tests',
          to: `/${currentStore?.value?.slug}/tests`,
        },
      currentStore?.value &&
        !store.multipleStoresSelected &&
        (can(can.teamMember.read) ||
          can(can.integrations.read) ||
          can(can.settingsGeneral.read) ||
          (capabilities.value?.splitTest && can(can.abTests.read))) && {
          rel: 'divider',
        },
      // Account Admin (ENG-5850): the flag is a global kill-switch since valet's LD client is anonymous.
      canAccessAccountAdmin.value && getFlag(FEATURE_FLAGS.ACCOUNT_PARENTS_ADMIN, false) && { rel: 'divider' },
      canAccessAccountAdmin.value &&
        getFlag(FEATURE_FLAGS.ACCOUNT_PARENTS_ADMIN, false) && {
          icon: Icons.LineBuilding,
          items: [
            { icon: Icons.LineRocket, label: 'Brands', to: '/account-admin/brands' },
            { icon: Icons.LineUsersAlt, label: 'Users', to: '/account-admin/users' },
            { icon: Icons.LineBuilding, label: 'Child Accounts', to: '/account-admin/child-accounts' },
            { icon: Icons.LineSetting, label: 'Settings', to: '/account-admin/settings' },
          ],
          label: 'Account Admin',
          rel: 'group',
        },
      can(can.developerApp.read) &&
        getFlag(FEATURE_FLAGS.DEVELOPER_PLATFORM, false) && {
          icon: Icons.LineCodeBranch,
          items: [
            {
              icon: Icons.ApiLogo,
              label: 'Apps',
              to: '/developer/apps',
            },
            // Organization + Collaborations are for true developer ORGs (third-party developers).
            // A merchant brand role (e.g. account_owner) who can also build apps for their OWN
            // stores manages people via the store's Team Members instead, and never collaborates
            // onto their own stores — so hide both for brand users.
            !user.isBrandUser &&
              can(can.teamMember.read) && {
                icon: Icons.LineUsersAlt,
                label: 'Organization',
                to: '/developer/organization',
              },
            !user.isBrandUser && {
              icon: Icons.LineCodeBranch,
              label: 'Collaborations',
              to: '/developer/collaborations',
            },
          ].filter(Boolean),
          label: 'Developer',
          rel: 'group',
        },
      (can(can.adminStores.read) ||
        can(can.adminUsers.read) ||
        can(can.whitelabel.list) ||
        can(can.globalMacro.read)) && {
        icon: Icons.LineFolder,
        items: [
          can(can.adminStores.read) && {
            icon: Icons.LineRocket,
            label: 'Stores',
            to: '/admin/stores',
          },
          can(can.adminUsers.read) && {
            icon: Icons.LineUsersAlt,
            label: 'Users',
            to: '/admin/users/active',
          },
          can(can.whitelabel.list) && {
            icon: Icons.LinePalette,
            label: 'Whitelabel',
            to: '/admin/whitelabel',
          },
          can(can.adminStores.read) &&
            getFlag(FEATURE_FLAGS.SEASONAL_THEMES, false) && {
              icon: Icons.LineMoon,
              label: 'Themes',
              to: '/admin/themes',
            },
          can(can.globalMacro.read) && {
            icon: Icons.LineBolt,
            label: 'Global Macros',
            to: '/admin/global-macros',
          },
          can(can.implementation.read) && {
            icon: Icons.LineGithubAlt,
            label: 'Implementation',
            to: '/admin/implementation/deployments',
          },
          can(can.engineering.read) &&
            getFlag(FEATURE_FLAGS.ENGINEERING_DASHBOARD, false) && {
              icon: Icons.LineCodeBranch,
              label: 'Engineering',
              to: '/admin/engineering/queues',
            },
          can(can.appReview.read) &&
            getFlag(FEATURE_FLAGS.DEVELOPER_PLATFORM, false) && {
              icon: Icons.ApiLogo,
              label: 'App Review',
              to: '/admin/app-review',
            },
          (can(can.adminStores.read) || can(can.engineering.read)) && {
            icon: Icons.LineSlack,
            label: 'Bot Pairings',
            to: '/admin/slack-bot',
          },
          can(can.claimAutomation.read) && {
            icon: Icons.LineRobot,
            items: [
              {
                // Control Panel, Insights, History, Feedback, Stores and Models are tabs on one page.
                activePaths: CLAIM_AUTOMATION_TAB_PATHS,
                icon: Icons.LineChart,
                label: 'Claim Automation',
                to: '/admin/claim-automation/insights',
              },
              {
                icon: Icons.LineComparison,
                label: 'Post-Claim',
                to: '/admin/claim-automation/post-claim',
              },
              {
                icon: Icons.LineUsdCircle,
                label: 'AI Spend',
                to: '/admin/claim-automation/ai-spend',
              },
              {
                icon: Icons.LineSetting,
                label: 'AI Models',
                to: '/admin/claim-automation/ai-models',
              },
            ].filter(Boolean) as NavItem[],
            label: 'AI Operations',
            rel: 'group',
          },
        ].filter(Boolean) as NavItem[],
        label: 'Admin',
        rel: 'group',
      },
    ].filter(Boolean) as NavItem[],
);

const links = useSeasonalNav(baseLinks);

const collapsed = inject('collapsed', true);
const storeCreditEnabled = computed(() => {
  return currentStore?.value?.generalSettings?.storeCreditType;
});

const atLeastOneWarrantyStore = computed(() => {
  return allStores?.value.some(store => store?.capabilities?.warranties);
});
</script>
<style lang="scss">
.fill-fosho {
  path {
    @apply fill-typography-disabled;
  }
}
</style>
